import React, {
  useReducer,
  useRef,
  useState,
  useEffect,
  MouseEventHandler,
} from 'react'
import { Transition } from 'react-transition-group'
import { gsap } from 'gsap'
import MobileNavModal from 'layouts/sidebar'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useNavigate, Link } from 'react-router-dom'
import { Popover, Transition as PopTransition } from '@headlessui/react'

gsap.registerPlugin(ScrollTrigger)
ScrollTrigger.defaults({})

let prevScroll = 0

export default function Header() {
  const [mobileNav, toggleMobileNav] = useReducer((s) => !s, false)
  const [toShowHeader, setToShowHeader] = useState(false)
  const [isTop, setIsTop] = useState(true)

  const navigate = useNavigate()

  const modalRef = useRef<HTMLDivElement>(null)
  const organizationsBtn = useRef<HTMLButtonElement>(null)
  const sustainabilityBtn = useRef<HTMLButtonElement>(null)
  const techBtn = useRef<HTMLButtonElement>(null)
  const farmersBtn = useRef<HTMLButtonElement>(null)
  const companyBtn = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (mobileNav) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [mobileNav])

  const handleScroll = (e: Event) => {
    const scrollPos = window.scrollY

    if (scrollPos > 60) {
      setIsTop(false)
    } else {
      setIsTop(true)
    }

    const prevScrollDiff = scrollPos - prevScroll

    if (prevScrollDiff > 1) {
      setToShowHeader(true)
    } else {
      setToShowHeader(false)
    }

    prevScroll = scrollPos
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const modalAnimation = (node: any) => {
    const tl = gsap.timeline()
    tl.fromTo(
      node,
      { opacity: 0, zIndex: -1 },
      { opacity: 1, duration: 0.3, zIndex: 200 },
    )
    tl.fromTo(
      modalRef.current,
      {
        opacity: 0,
        xPercent: 100,
      },
      {
        xPercent: 0,
        opacity: 1,
        duration: 0.3,
      },
    )
    return tl
  }

  const popNavigation = (
    target: string,
    popId: 'farmers' | 'organizations' | 'sustainability' | 'company' | 'tech',
  ) => {
    switch (popId) {
      case 'farmers': {
        navigate(target)
        return farmersBtn.current?.click()
      }
      case 'organizations': {
        navigate(target)
        return organizationsBtn.current?.click()
      }
      case 'sustainability': {
        navigate(target)
        return sustainabilityBtn.current?.click()
      }
      case 'company': {
        navigate(target)
        return companyBtn.current?.click()
      }
      case 'tech': {
        navigate(target)
        return techBtn.current?.click()
      }
      default:
    }
  }

  return (
    <>
      <Transition
        appear
        timeout={500}
        in={mobileNav}
        onEnter={(node: any) => modalAnimation(node).play()}
        onExit={(node: any) => modalAnimation(node).reverse(0)}
        mountOnEnter
        unmountOnExit
      >
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-opacity-50 min-[1100px]:hidden block z-[9999] bg-black"
          // onClick={() => {
          //   toggleMobileNav()
          // }}
        >
          <div
            ref={modalRef}
            className="h-full overflow-y-auto z-[9999] bg-green max-w-md ml-auto"
          >
            <MobileNavModal toggle={toggleMobileNav} />
          </div>
        </div>
      </Transition>

      <header
        className={`fixed top-0 left-0 lg:py-2 xl:py-0 right-0 z-50 transition-all duration-500 
        ${toShowHeader ? '-translate-y-[110%] lg:-translate-y-[390%]' : ''}
        ${!isTop ? 'bg-[#fff]' : 'bg-transparent'}
        `}
      >
        <nav className="w-container-lg flex flex-nowrap top-0 left-0 right-0 bottom-0 items-center gap-x-10 z-100 py-3 2xl:py-4">
          <div className=" max-w-md">
            <Link to="/" className="outline-lime-400 outline-offset-4">
              {/* <img
                src={isTop ? logoWhite : logo}
                alt="BharatRohan Logo"
                title="BharatRohan Logo"
                className="h-[2.5rem] w-full min-h-fit min-w-fit sm:h-14 xl:h-16"
              /> */}
              <svg
                version="1.2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 783 183"
                className="h-[2.5rem] lg:h-12 2xl:h-16 w-auto"
              >
                <title>BharatRohan's Logo</title>
                <defs>
                  <linearGradient
                    id="g1"
                    x2="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="matrix(92.681,61.069,-14.963,22.708,4.25,124.52)"
                  >
                    <stop offset="0" stopColor="#8dbe3f" />
                    <stop offset=".166" stopColor="#86b53d" />
                    <stop offset=".428" stopColor="#749e38" />
                    <stop offset=".752" stopColor="#567a30" />
                    <stop offset="1" stopColor="#3c5929" />
                  </linearGradient>
                  <linearGradient
                    id="g2"
                    x2="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="matrix(92.681,61.069,-23.883,36.246,6.003,121.86)"
                  >
                    <stop offset="0" stopColor="#8dbe3f" />
                    <stop offset=".166" stopColor="#86b53d" />
                    <stop offset=".428" stopColor="#749e38" />
                    <stop offset=".752" stopColor="#567a30" />
                    <stop offset="1" stopColor="#3c5929" />
                  </linearGradient>
                  <linearGradient
                    id="g3"
                    x2="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="matrix(-92.681,0,0,-60.003,185.09,-424)"
                  >
                    <stop offset="0" stopColor="#8dbe3f" />
                    <stop offset=".166" stopColor="#86b53d" />
                    <stop offset=".428" stopColor="#749e38" />
                    <stop offset=".752" stopColor="#567a30" />
                    <stop offset="1" stopColor="#3c5929" />
                  </linearGradient>
                  <linearGradient
                    id="g4"
                    x2="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="matrix(-92.681,61.069,-23.883,-36.246,183.338,121.86)"
                  >
                    <stop offset="0" stopColor="#8dbe3f" />
                    <stop offset=".166" stopColor="#86b53d" />
                    <stop offset=".428" stopColor="#749e38" />
                    <stop offset=".752" stopColor="#567a30" />
                    <stop offset="1" stopColor="#3c5929" />
                  </linearGradient>
                </defs>
                <path
                  id="Layer"
                  fillRule="evenodd"
                  className="fill-none"
                  d="m12.4 116q-0.1 0-0.2 0 0.1 0 0.2 0z"
                />
                <path
                  id="Layer"
                  className="fill-[#d69846]"
                  d="m127.4 124.6c15.3-15.3 34.4-26.7 55.5-32.8q0 0.1 0 0.2c0-5.4-1-13.9-1-13.2-23.6 7.7-44.5 22.1-60.1 41q3 2.2 5.6 4.8 0 0 0 0z"
                />
                <path
                  id="Layer"
                  className="fill-[#daa543]"
                  d="m115.5 115.9c15.2-23 37.4-40.6 63.4-50.2q-1-3.4-2.6-7.2-1.4-3.4-2.9-6.3c-28.1 11.9-51.4 33.4-65.3 60.7q3.8 1.2 7.4 3 0 0 0 0z"
                />
                <path
                  id="Layer"
                  className="fill-[#e1af3f]"
                  d="m101.6 111.4q0 0 0 0c12.2-30.9 35.9-55.8 65.6-69.6q-4.2-6-9.3-11.2c-31 16.4-54.8 45.2-64.3 80.3q0.5 0 0.9 0 3.6 0 7.1 0.5z"
                />
                <path
                  id="Layer"
                  className="fill-yellow"
                  d="m87.1 111.5q0 0 0.1 0c6.4-38.4 29.5-70.5 61.2-89.4-4.1-3.1-8.5-5.9-13-8.3-30.8 21.3-52.2 55.6-55.4 95.7q-0.2 1.9-0.3 3.8 3.6-1.2 7.4-1.8z"
                />
                <path
                  id="Layer"
                  className="fill-[#efc639]"
                  d="m72.8 116.3q-0.5-9.1 0.2-18.5c0.6-7.7 2.5-21.8 8.9-37.7 10.7-26.7 28.4-43.7 38.8-52.3-2.7-1-5.9-1.7-9.7-2.4-3.5-0.7-7-1.2-9.8-1.3-8.5 8.5-20.6 22.9-28.9 43.7-3 7.4-7.3 20.4-8.7 37.2-1 12.4-0.2 24.4 2.1 36q3.3-2.7 7.1-4.7 0 0 0 0z"
                />
                <path
                  id="Layer"
                  className="fill-[#f3d433]"
                  d="m60.1 126.2c-5.3-15.9-7.5-33.1-6.1-50.9 2.2-26.8 12.5-51.1 28.3-70.6-7.8 1.1-15.2 3.2-22.2 6.1-9.6 16.3-15.7 34.9-17.4 55-1.8 22.4 2.3 44 10.9 63.2q1.5 0.8 3 1.6 1.6-2.3 3.5-4.4z"
                />
                <path
                  id="Layer"
                  className="fill-[#f7df2f]"
                  d="m43.8 124.5q0 0 0 0c-9.4-19.8-13.9-42.3-12-65.7 1-12.7 3.9-24.9 8.3-36.2-9.3 7.3-17 16.3-22.8 26.6q-0.1 1-0.2 2c-2 24.6 3.1 48.2 13.5 68.7q0 0 0 0 6.8 1.9 13.2 4.6z"
                />
                <path
                  id="Layer"
                  className="fill-[#d18a49]"
                  d="m132.8 131.1c11.5-6.2 23.9-10.6 37.1-13.1q5-1.5 10.2-2.7 1.6-5.6 2.4-11.4c-18.8 4.7-36.1 13.6-50.8 25.7q0 0 0 0 0.6 0.7 1.1 1.5z"
                />
                <path
                  id="Layer"
                  className="fill-[#fbe625]"
                  d="m119.3 133.5q0 0 0 0-2.2-2.2-4.7-4 0 0 0 0-2.5-1.8-5.3-3.2 0 0 0 0-3-1.4-6.2-2.2 0 0 0 0-2.7-0.7-5.5-1 0 0 0 0-1.5-0.1-3.1-0.1-1.8 0-3.5 0.2 0 0 0 0-2.8 0.3-5.3 1 0 0 0 0-3.1 0.8-6 2.2 0 0 0 0-2.8 1.3-5.3 3.2 0 0 0 0-2.9 2.1-5.3 4.7 0 0 0 0-1.1 1.2-2.1 2.6 2 1.4 4 2.8 0 0 0 0 3.6 2.6 7 5.5 0 0 0 0 2.6 2.2 5 4.4 0 0 0 0 2.1 2 4 4 0 0 0 0 1.4 1.4 2.7 2.8 0 0 0 0 1.5 1.6 2.9 3.3 0 0 0 0 1 1.1 1.9 2.2 0 0 0 0 0.1 0.1 0.1 0.2 1.3-1.5 2.6-3 0 0 0 0 2.1-2.3 4.2-4.5 3.8-3.9 8-7.4 0-0.1 0-0.1 6.2-5.3 13-9.8-1.4-2-3.1-3.8z"
                />
                <path
                  id="Layer"
                  className="fill-[#fbe625]"
                  d="m12.2 116q0.1 0 0.2 0 0 0 0 0 1.4 0.2 2.7 0.5 2.8 0.4 5.6 1c-6.5-12.5-11.1-26.1-13.2-40.4-0.8 4.8-1.3 9.8-1.3 14.9 0 8.2 1.1 16.1 3.3 23.7q0 0 0 0 1.3 0.1 2.7 0.3z"
                />
                <path
                  id="Layer"
                  className="fill-[#ffffff]"
                  d="m12.4 116q0 0 0 0 4.2 0.6 8.3 1.5 0 0 0 0-2.8-0.6-5.6-1-1.3-0.3-2.7-0.5z"
                />
                <path
                  id="Layer"
                  className="fill-[#ffffff]"
                  d="m12.2 116q-1.4-0.2-2.7-0.3 0 0 0 0 0 0 0 0 1.3 0.1 2.7 0.3z"
                />
                <path
                  id="Layer"
                  className="fill-[url(#g1)]"
                  d="m94.5 180.4l-0.1-0.3q0 0 0 0 0 0 0 0 0 0 0 0 0-0.1-0.1-0.1 0 0 0-0.1-0.1 0-0.1-0.1-0.1-0.1-0.2-0.2-0.2-0.3-0.3-0.5-0.1-0.1-0.2-0.2-0.2-0.3-0.4-0.6-0.5-0.6-0.9-1.2-0.4-0.5-0.9-1.1-0.5-0.6-1-1.3-0.3-0.4-0.7-0.9-0.6-0.7-1.2-1.4-0.2-0.3-0.5-0.6-0.7-0.9-1.5-1.7 0 0 0 0-3.8-4.4-7.9-8.3 0 0 0 0.1-5.4-5.2-11.4-9.7 0 0 0 0-2.7-2.1-5.6-4.1-1.3-0.8-2.6-1.7-1.3-0.8-2.5-1.6-0.1 0-0.2-0.1-0.3-0.1-0.6-0.3 0 0 0 0-2.3-1.4-4.7-2.7 0-0.1 0-0.1-4.9-2.6-10.1-4.9c-8.5-3.7-17.5-6.5-26.9-8.4 10.6 23.7 31.3 41.9 56.6 49.1q0.4 0.1 0.9 0.2 0.1 0 0.1 0.1c7.3 1.9 14.9 2.9 22.8 2.9q0.2 0 0.4 0z"
                />
                <path
                  id="Layer"
                  className="fill-[url(#g2)]"
                  d="m94.4 180.1q0 0 0 0 0 0 0 0 0 0 0 0z"
                />
                <path
                  id="Layer"
                  className="fill-[url(#g3)]"
                  d="m94.8 180.4l0.1-0.3h0.1q0 0 0 0 0 0 0 0 0 0 0 0 0-0.1 0-0.1 0 0 0.1-0.1 0 0 0-0.1 0.1-0.1 0.2-0.2 0.2-0.3 0.4-0.5 0-0.1 0.1-0.2 0.2-0.3 0.5-0.6 0.4-0.6 0.9-1.2 0.4-0.5 0.8-1.1 0.5-0.6 1-1.3 0.4-0.4 0.8-0.9 0.5-0.7 1.1-1.4 0.3-0.3 0.5-0.6 0.8-0.9 1.5-1.7 0 0 0 0 3.8-4.4 7.9-8.3 0 0 0 0.1 5.4-5.2 11.4-9.7 0 0 0 0 2.8-2.1 5.7-4.1 1.2-0.8 2.6-1.7 1.2-0.8 2.5-1.6 0.1 0 0.2-0.1 0.2-0.1 0.5-0.3 0 0 0 0 2.4-1.4 4.8-2.7 0-0.1 0-0.1 4.9-2.6 10-4.9c8.6-3.7 17.6-6.5 26.9-8.4-10.5 23.7-31.2 41.9-56.6 49.1q-0.4 0.1-0.9 0.2 0 0-0.1 0.1c-7.2 1.9-14.8 2.9-22.7 2.9q-0.2 0-0.4 0z"
                />
                <path
                  id="Layer"
                  className="fill-[url(#g4)]"
                  d="m94.9 180.1q0.1 0 0.1 0 0 0 0 0 0 0-0.1 0z"
                />
                <path
                  id="Layer"
                  fillRule="evenodd"
                  className={`${
                    isTop && location.pathname !== '/careers'
                      ? 'fill-white'
                      : 'fill-green'
                  } transition-all duration-300`}
                  d="m342.5 82.2c4.9-1.5 12.2-2.4 18.8-2.4 15.1 0 22.2 5.8 22.2 14.9 0 1.9-0.3 4-0.9 6.3l-6.7 25h-18.9l1.2-4.6c-4.1 3.4-7.5 5.6-12.8 5.6-7 0-12.2-4.6-12.2-12.4 0-9.6 7.5-15.9 20.4-15.9 3.4 0 7 0.5 10.4 1.5l0.2-0.5c0.1-0.5 0.3-1.5 0.3-2.2 0-2.8-2.3-4.4-7-4.4-4.2 0-9.4 0.8-12.5 2q-1.2 0.4-2.4 0.9-0.9 0.4-1.9 1c-1.1 0.7-2 1.5-2.8 2.2m24.1 9.7l0.5-1.9c-1.3-0.3-2.3-0.5-3.5-0.5-4.8 0-7.5 2.2-7.5 6.2 0 2.3 1.4 3.6 3.1 3.6 3 0 6.3-3.1 7.4-7.4zm-68.7-43.8h18.9l-5.6 21c3-3.6 7.9-6.7 13.6-6.7 8.3 0 13 5.4 13 13.9 0 2.4-0.3 4.9-1.1 7.7l-6.7 25h-18.9l6.1-22.7c0.3-1.1 0.5-2.5 0.5-3.4 0-2.4-1.3-4-4-4-3.8 0-5.8 2.5-7.1 7.4l-6.1 22.7h-19zm100 15.4h18.9l-2.4 9.1c4.3-6.1 9.5-10.1 16.9-9.8l-5.3 20.1h-2.1c-8.9 0-13.7 4.7-16.3 14.8l-3 11.3h-18.9zm82.4 34.1c0-1.9 0.4-4 1-6.3l3.5-13.2h-5.4l4-14.6h5.3l3.1-11.4h19l-3.1 11.4h10.5l-3.9 14.6h-10.5l-2.6 9.8c-0.5 1.5-0.6 2.5-0.6 3.5 0 1.9 1.1 2.9 3.8 2.9 1.5 0 2.9-0.3 5.5-1l-4.1 15.4c-3.2 0.8-6.4 1.3-9.6 1.3-10.7 0-15.9-4-15.9-12.4zm94.5-8.7c0-13.9 11.5-26.5 27.4-26.5 14.3 0 22.8 9.4 22.8 21.2 0 13.9-11.5 26.5-27.4 26.5-14.3 0-22.8-9.3-22.8-21.2zm32.3-4.5c0-3.8-2.4-6.6-6.1-6.6-4.8 0-8.3 4.9-8.3 10.3 0 3.8 2.4 6.6 6.1 6.6 4.8 0 8.3-4.8 8.3-10.3zm31.4-36.2h19l-5.7 21c3-3.7 7.9-6.8 13.6-6.8 8.4 0 13.1 5.4 13.1 13.9 0 2.4-0.4 5-1.1 7.7l-6.7 25h-19l6.1-22.6c0.4-1.2 0.5-2.5 0.5-3.5 0-2.4-1.2-4-4-4-3.8 0-5.8 2.5-7.1 7.5l-6.1 22.6h-19zm100 15.4h19l-1.5 5.7c3-3.7 7.9-6.8 13.6-6.8 8.4 0 13.1 5.4 13.1 13.9 0 2.4-0.4 5-1.1 7.7l-6.7 25h-19l6.1-22.7c0.3-1.1 0.5-2.5 0.5-3.4 0-2.4-1.3-4-4-4-3.8 0-5.8 2.5-7.1 7.4l-6.1 22.7h-19zm-456.5 27.8c0 9.8-7.9 17.8-27.9 17.8h-31.9l15.7-58.4h25.9c18 0 22.4 6.6 22.4 13.7 0 7.1-4.8 13.1-13.7 15.4 5.5 1.4 9.5 5.5 9.5 11.5zm-14.9-22.4c0-2.4-1.8-3.4-6-3.4h-8.1l-2.2 8.1-3.3 12-2.3 8.7h9.4c4.8 0 7.7-2.1 7.7-5 0-2.5-2.1-3.7-6.3-3.7h-3.6l3.3-12h4.1c5 0 7.3-2.2 7.3-4.7zm292.1 20.7l8.1 19.5h-21.1l-6.5-16.7h-0.1l3.8-13.9h2.2c6.6 0 10-3 10-6.7 0-1.2-0.4-2.3-1.3-3.1-1-1.1-3-1.7-5.7-1.7h-7l-2.5 9.5-8.9 32.6h-19.4l15.7-58.4h22.8c10.8 0 17.7 2.3 21.6 6.2 3.3 3.3 4.6 7.6 4.6 12.2 0 9.1-4.8 17-16.3 20.5zm-124-24.3c4.9-1.5 12.2-2.4 18.8-2.4 15.1 0 22.2 5.7 22.2 14.8 0 1.9-0.3 4.1-0.9 6.4l-6.7 25h-19l1.3-4.6c-4.1 3.4-7.5 5.6-12.8 5.6-7 0-12.2-4.6-12.2-12.5 0-9.5 7.5-15.8 20.4-15.8 3.4 0 6.9 0.5 10.4 1.5l0.1-0.5c0.2-0.6 0.4-1.5 0.4-2.3 0-2.8-2.3-4.4-7-4.4-4.2 0-9.4 0.9-12.5 2l-2.4 0.9q-0.9 0.4-1.9 1c-1.1 0.7-2.1 1.5-2.8 2.3zm19.5 26.6l0.5-1.9c-1.3-0.3-2.3-0.5-3.5-0.5-4.8 0-7.5 2.3-7.5 6.2 0 2.3 1.4 3.6 3.1 3.6 3 0 6.3-3.1 7.4-7.4zm233.1-26.8c4.9-1.5 12.2-2.4 18.8-2.4 15.1 0 22.1 5.7 22.1 14.8 0 1.9-0.2 4.1-0.8 6.4l-6.7 25h-19l1.3-4.6c-4.1 3.4-7.5 5.6-12.8 5.6-7 0-12.2-4.6-12.2-12.4 0-9.6 7.5-15.9 20.4-15.9 3.4 0 6.9 0.5 10.4 1.5l0.1-0.5c0.2-0.6 0.4-1.5 0.4-2.2 0-2.9-2.3-4.5-7.1-4.5-4.1 0-9.3 0.9-12.4 2l-2.4 0.9q-0.9 0.4-1.9 1c-1.1 0.8-2.1 1.5-2.8 2.3zm19.5 26.6l0.5-1.9c-1.3-0.3-2.3-0.5-3.5-0.5-4.8 0-7.5 2.3-7.5 6.2 0 2.3 1.4 3.6 3.1 3.6 3 0 6.3-3.1 7.4-7.4z"
                />
              </svg>
            </Link>
          </div>
          <div className=" min-[1100px]:border min-[1100px]:border-green max-w-fit ml-auto bg-white min-[1100px]:py-[0.75rem] min-[1100px]:px-7 flex-1 rounded-full flex-nowrap items-center min-[1100px]:gap-x-4 xl:gap-x-7 xl:py-3.5 justify-around min-[1100px]:flex 2xl:py-4">
            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={techBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center "
                  >
                    Technology
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3.5"
                      stroke="#163e33"
                      className={` ${
                        open ? 'rotate-180' : ''
                      } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Popover.Button>

                  <PopTransition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                    className="absolute   top-16 2xl:top-[4.75rem]"
                  >
                    <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                      <div className="basis-96 grow shrink  flex flex-col p-2 divide-y-1 divide-slate-200 gap-1">
                        <PopOption
                          text="UAVs"
                          callback={() =>
                            popNavigation('/technology?tech=uav', 'tech')
                          }
                        />
                        <PopOption
                          text="SaaS"
                          callback={() =>
                            popNavigation('/technology?tech=saas', 'tech')
                          }
                        />
                      </div>
                    </Popover.Panel>
                  </PopTransition>
                </>
              )}
            </Popover>

            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={farmersBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    For farmers
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3.5"
                      stroke="#163e33"
                      className={` ${
                        open ? 'rotate-180' : ''
                      } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Popover.Button>

                  <PopTransition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                    className="absolute  top-16 2xl:top-[4.75rem]"
                  >
                    <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                      <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                        <PopOption
                          text="CropAssure®"
                          callback={() =>
                            popNavigation('/crop-assure', 'farmers')
                          }
                        />
                        <PopOption
                          text="Pricing"
                          callback={() =>
                            popNavigation(
                              '/crop-assure#crop-pricing',
                              'farmers',
                            )
                          }
                        />
                      </div>
                    </Popover.Panel>
                  </PopTransition>
                </>
              )}
            </Popover>

            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={organizationsBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    For organisations
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3.5"
                      stroke="#163e33"
                      className={` ${
                        open ? 'rotate-180' : ''
                      } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Popover.Button>

                  <PopTransition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                    className="absolute   top-16 2xl:top-[4.75rem]"
                  >
                    <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                      <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                        <Popover className={'hidden min-[1100px]:block'}>
                          {({ open }) => (
                            <>
                              <Popover.Button className="py-2 text-dark flex flex-nowrap items-center gap-1 group stroke-violet fill-violet font-semibold  outline-none focus:outline-none">
                                SourceAssure®
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="3.5"
                                  stroke="#163e33"
                                  className={` ${
                                    open ? 'rotate-0' : '-rotate-90'
                                  } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                  />
                                </svg>
                              </Popover.Button>

                              <PopTransition
                                show={open}
                                enter="transition duration-100 ease-out"
                                enterFrom="transform scale-95 opacity-0"
                                enterTo="transform scale-100 opacity-100"
                                leave="transition duration-75 ease-out"
                                leaveFrom="transform scale-100 opacity-100"
                                leaveTo="transform scale-95 opacity-0"
                                className="absolute right-full top-4"
                              >
                                <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                                  <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                                    <PopOption
                                      text="SourceAssure®"
                                      callback={() =>
                                        popNavigation(
                                          '/source-assure',
                                          'organizations',
                                        )
                                      }
                                    />
                                    <Popover
                                      className={'hidden min-[1100px]:block'}
                                    >
                                      {({ open }) => (
                                        <>
                                          <Popover.Button className="py-2 text-dark flex flex-nowrap items-center gap-1 group stroke-violet fill-violet font-semibold  outline-none focus:outline-none">
                                            Products
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              strokeWidth="3.5"
                                              stroke="#163e33"
                                              className={` ${
                                                open ? 'rotate-0' : '-rotate-90'
                                              } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                              />
                                            </svg>
                                          </Popover.Button>

                                          <PopTransition
                                            show={open}
                                            enter="transition duration-100 ease-out"
                                            enterFrom="transform scale-95 opacity-0"
                                            enterTo="transform scale-100 opacity-100"
                                            leave="transition duration-75 ease-out"
                                            leaveFrom="transform scale-100 opacity-100"
                                            leaveTo="transform scale-95 opacity-0"
                                            className="absolute top-[101%] right-0"
                                          >
                                            <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                                              <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                                                <PopOption
                                                  text="Cumin"
                                                  callback={() =>
                                                    popNavigation(
                                                      '/products/cumin',
                                                      'organizations',
                                                    )
                                                  }
                                                />
                                                <PopOption
                                                  text="Fennel"
                                                  callback={() =>
                                                    popNavigation(
                                                      '/products/fennel',
                                                      'organizations',
                                                    )
                                                  }
                                                />
                                                <PopOption
                                                  text="Coriander"
                                                  callback={() =>
                                                    popNavigation(
                                                      '/products/coriander',
                                                      'organizations',
                                                    )
                                                  }
                                                />
                                                <PopOption
                                                  text="Fenugreek"
                                                  callback={() =>
                                                    popNavigation(
                                                      '/products/fenugreek',
                                                      'organizations',
                                                    )
                                                  }
                                                />
                                                <PopOption
                                                  text="Mustard"
                                                  callback={() =>
                                                    popNavigation(
                                                      '/products/mustard',
                                                      'organizations',
                                                    )
                                                  }
                                                />
                                              </div>
                                            </Popover.Panel>
                                          </PopTransition>
                                        </>
                                      )}
                                    </Popover>
                                  </div>
                                </Popover.Panel>
                              </PopTransition>
                            </>
                          )}
                        </Popover>
                        <PopOption
                          text="SeedAssure®"
                          callback={() =>
                            popNavigation('/seed-assure', 'organizations')
                          }
                        />
                      </div>
                    </Popover.Panel>
                  </PopTransition>
                </>
              )}
            </Popover>

            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={sustainabilityBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    Sustainability
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3.5"
                      stroke="#163e33"
                      className={` ${
                        open ? 'rotate-180' : ''
                      } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Popover.Button>

                  <PopTransition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                    className="absolute   top-16 2xl:top-[4.75rem]"
                  >
                    <Popover.Panel className="md:w-[20rem] p-3 rounded-md grid  gap-x-6 bg-white relative right-[60%]">
                      <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                        <PopOption
                          text="How we see sustainability?"
                          callback={() =>
                            popNavigation('/sustainability', 'sustainability')
                          }
                        />
                        <PopOption
                          text="Supply chain traceability"
                          callback={() =>
                            popNavigation('/supply-chain', 'sustainability')
                          }
                        />
                        <PopOption
                          text="Carbon farming"
                          callback={() =>
                            popNavigation('/carbon-farming', 'sustainability')
                          }
                        />
                        <PopOption
                          text="IPM implementations"
                          callback={() =>
                            popNavigation('/ipm', 'sustainability')
                          }
                        />
                      </div>
                    </Popover.Panel>
                  </PopTransition>
                </>
              )}
            </Popover>

            {/* <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={companyBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    <div onClick={()=>{navigate('/investors')}}>Investor Relations</div>
                  
                  </Popover.Button>
                </>
              )}
            </Popover> */}

            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={companyBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    About us
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3.5"
                      stroke="#163e33"
                      className={` ${
                        open ? 'rotate-180' : ''
                      } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </Popover.Button>

                  <PopTransition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                    className="absolute   top-16 2xl:top-[4.75rem]"
                  >
                    <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white relative right-[60%]">
                      <div className="basis-96  grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                        <PopOption
                          text="Our company"
                          callback={() => popNavigation('/about-us', 'company')}
                        />
                        <Popover className={'hidden min-[1100px]:block'}>
                          {({ open }) => (
                            <>
                              <Popover.Button
                                tabIndex={0}
                                className="py-2 text-dark flex flex-nowrap items-center gap-1 group stroke-violet fill-violet font-semibold  outline-none focus:outline-none"
                              >
                                Resources
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth="3.5"
                                  stroke="#163e33"
                                  className={` ${
                                    open ? 'rotate-0' : '-rotate-90'
                                  } transition-all duration-300 ease-in-out w-3.5 h-3.5 xl:w-4 xl:h-4`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                  />
                                </svg>
                              </Popover.Button>

                              <PopTransition
                                show={open}
                                enter="transition duration-100 ease-out"
                                enterFrom="transform scale-95 opacity-0"
                                enterTo="transform scale-100 opacity-100"
                                leave="transition duration-75 ease-out"
                                leaveFrom="transform scale-100 opacity-100"
                                leaveTo="transform scale-95 opacity-0"
                                className="absolute right-full top-20"
                              >
                                <Popover.Panel className="md:w-[15rem] p-3 rounded-md grid  gap-x-6 bg-white">
                                  <div className="basis-96 grow shrink  flex flex-col px-2 divide-y-1 divide-slate-200 gap-1">
                                    <PopOption
                                      text="Case studies"
                                      callback={() =>
                                        popNavigation(
                                          '/case-studies',
                                          'company',
                                        )
                                      }
                                    />
                                    <PopOption
                                      text="Blogs and updates"
                                      callback={() =>
                                        popNavigation(
                                          '/blogs-and-updates',
                                          'company',
                                        )
                                      }
                                    />
                                  </div>
                                </Popover.Panel>
                              </PopTransition>
                            </>
                          )}
                        </Popover>

                        <PopOption
                          text="Contact us"
                          callback={() =>
                            popNavigation('/contact-us', 'company')
                          }
                        />
                        <PopOption
                          text="Careers"
                          callback={() => popNavigation('/careers', 'company')}
                        />
                      </div>
                    </Popover.Panel>
                  </PopTransition>
                </>
              )}
            </Popover>
            <Popover className={'hidden min-[1100px]:block'}>
              {({ open }) => (
                <>
                  <Popover.Button
                    ref={companyBtn}
                    className="focus:outline-none text-base lg:text-[0.9rem] text-black flex flex-nowrap gap-1 items-center outline-offset-4 "
                  >
                    <div onClick={()=>{navigate('/csr')}}>CSR Initiatives</div>
                  </Popover.Button>
                </>
              )}
            </Popover>
            

            <div className="flex gap-2 min-[1100px]:hidden">
              <button
                aria-label="Menu"
                onClick={() => {
                  toggleMobileNav()
                }}
                type="button"
                className="rounded-full  p-2 md:p-3 hover:bg-gray-200 outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="#193C34"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </nav>
      </header>
    </>
  )
}

function PopOption({
  callback,
  text,
}: {
  text: string
  callback: MouseEventHandler<HTMLButtonElement>
}) {
  return (
    <button
      type="button"
      aria-label={text}
      onClick={callback}
      className="outline-lime-400 outline-offset-4 py-2 flex flex-nowrap items-center gap-1 group stroke-violet fill-violet font-semibold "
    >
      <span className="group-hover:text-violet group-focus:text-violet text-dark">
        {text}
      </span>
    </button>
  )
}
